import { BRAND } from "@/lib/constants";
import { Instagram, ArrowUpRight, Play, Heart } from "lucide-react";
import { motion } from "framer-motion";

// Real work tiles — curated from uploaded assets
const TILES = [
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/fnvfaxzf_IMG_3305.JPG.jpeg",
    tag: "City Series",
    label: "Mumbai · Gateway",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/wssipa5q_WhatsApp%20Image%202026-04-18%20at%2013.07.06.jpeg",
    tag: "Reel",
    label: "Kuldeep",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/snzi2s2m_WhatsApp%20Image%202026-04-22%20at%2015.36.31.jpeg",
    tag: "Cocktail",
    label: "Starlit Gala",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/ijwqjd1e_WhatsApp%20Image%202026-04-18%20at%2013.07.06.jpeg",
    tag: "Save-the-Date",
    label: "Devanshi",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/buyzfj3n_WhatsApp%20Image%202026-04-24%20at%2022.07.12.jpeg",
    tag: "Mandap Design",
    label: "Temple Vision",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/mnpfelf3_WhatsApp%20Image%202026-04-18%20at%2013.07.06.jpeg",
    tag: "Reel",
    label: "Nishika",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/5zfsxqqn_WhatsApp%20Image%202026-02-14%20at%2023.56.46%20-%20Copy.jpeg",
    tag: "Stationery",
    label: "Welcome Sign",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_portfolio-landing-49/artifacts/h5dd1tf7_WhatsApp%20Image%202026-04-18%20at%2014.09.09.jpeg",
    tag: "Destination",
    label: "Luggage Tags",
  },
];

export default function InstagramGrid() {
  return (
    <section
      id="instagram"
      data-testid="instagram-section"
      className="relative py-24 md:py-36 border-t border-white/5 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #1e1a16 0%, #1f1a1c 100%)",
      }}
    >
      {/* Ambient glows */}
      <div
        aria-hidden="true"
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(232,165,180,0.22) 0%, rgba(0,0,0,0) 70%)",
          filter: "blur(30px)",
        }}
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 right-0 w-[500px] h-[500px] pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(212,175,55,0.14) 0%, rgba(0,0,0,0) 65%)",
          filter: "blur(30px)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Editorial Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-16 md:mb-20"
        >
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[var(--iw-gold)]" />
            <span className="overline text-[var(--iw-gold)]">
              Live From Our Feed
            </span>
            <span className="w-8 h-px bg-[var(--iw-gold)]" />
          </div>

          <h2
            className="font-serif-display text-[var(--iw-ivory)] text-[48px] md:text-[88px] leading-[0.92] tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Watch every invite
            <br />
            <span className="italic gold-rose-gradient">go viral.</span>
          </h2>

          <p className="text-[var(--iw-ivory-dim)] text-base md:text-lg leading-relaxed max-w-xl mx-auto mt-8 font-light">
            Daily reels, behind-the-scenes and real client reactions —
            follow our atelier on Instagram for inspiration before you book.
          </p>

          {/* Handle + Follow CTA */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 inline-flex flex-wrap items-center justify-center gap-4 md:gap-6"
          >
            <a
              href={BRAND.instagramUrl}
              target="_blank"
              rel="noreferrer"
              className="group relative inline-flex items-center gap-3 px-8 py-4 overflow-hidden transition-transform duration-500 hover:scale-[1.02]"
              style={{
                background:
                  "linear-gradient(135deg, #E8A5B4 0%, #D4AF37 50%, #b87a70 100%)",
                color: "#0a0a0a",
                boxShadow: "0 20px 60px -15px rgba(232,165,180,0.5)",
              }}
              data-testid="instagram-main-cta"
            >
              <Instagram size={18} strokeWidth={2} />
              <span
                className="font-serif-display italic text-xl md:text-2xl"
                style={{ fontFamily: "Cormorant Garamond, serif" }}
              >
                @{BRAND.instagram.toLowerCase()}
              </span>
              <ArrowUpRight
                size={18}
                className="ml-1 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </a>
            <div className="flex items-center gap-2 text-[var(--iw-faint)]">
              <Heart size={13} className="text-[var(--iw-rose)]" fill="currentColor" />
              <span className="overline">500k+ reels viewed</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Grid — 4 cols, real work tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5">
          {TILES.map((t, i) => (
            <motion.a
              key={i}
              href={BRAND.instagramUrl}
              target="_blank"
              rel="noreferrer"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{
                duration: 0.8,
                delay: 0.05 * i,
                ease: [0.16, 1, 0.3, 1],
              }}
              className="relative aspect-square overflow-hidden border border-white/10 hover:border-[var(--iw-gold)]/50 transition-colors group"
              data-testid={`instagram-tile-${i}`}
            >
              <img
                src={t.src}
                alt={`${t.label} · ${t.tag} — InvitationWala digital wedding invitation showcase`}
                className="w-full h-full object-cover transition-transform duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-110"
                loading="lazy"
                decoding="async"
              />
              {/* Gradient overlay */}
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(10,10,10,0) 50%, rgba(10,10,10,0.85) 100%)",
                }}
              />
              {/* Tag */}
              <span
                className="absolute top-3 left-3 overline px-2.5 py-1 border text-[10px]"
                style={{
                  background: "rgba(212,175,55,0.15)",
                  borderColor: "rgba(212,175,55,0.5)",
                  color: "var(--iw-gold)",
                }}
              >
                {t.tag}
              </span>
              {/* Play-style pill for reel vibe */}
              <span className="absolute top-3 right-3 w-8 h-8 rounded-full grid place-items-center bg-white/10 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-500 -translate-y-1 group-hover:translate-y-0">
                <Play
                  size={12}
                  className="text-white ml-0.5"
                  fill="currentColor"
                />
              </span>
              {/* Label */}
              <div className="absolute bottom-3 left-3 right-3">
                <div
                  className="font-serif-display text-[var(--iw-ivory)] text-lg leading-tight"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {t.label}
                </div>
              </div>
              {/* Hover overlay with IG icon */}
              <div className="absolute inset-0 grid place-items-center bg-black/0 group-hover:bg-black/40 transition-colors duration-500">
                <Instagram
                  className="text-[var(--iw-gold)] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  size={32}
                  strokeWidth={1.25}
                />
              </div>
            </motion.a>
          ))}
        </div>

        {/* Secondary CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-14 text-center"
        >
          <a
            href={BRAND.instagramUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 overline text-[var(--iw-faint)] hover:text-[var(--iw-gold)] transition-colors"
            data-testid="instagram-see-more"
          >
            See every reel on Instagram
            <ArrowUpRight size={12} />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
